import { Circle, G, Path, Polygon, StyleSheet, Svg, Text, View } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";

import { PdfActivityIcon } from "src/core/components/pdf/icons/radar/PdfActivityIcon";
import { PdfClientIcon } from "src/core/components/pdf/icons/radar/PdfClientIcon";
import { PdfLocationIcon } from "src/core/components/pdf/icons/radar/PdfLocationIcon";
import { PdfMissionIcon } from "src/core/components/pdf/icons/radar/PdfMissionIcon";
import { CustomRiskLevel } from "src/core/constants/risk";
import { CartographyRisks } from "src/core/contexts/FolderContext";

const styles = StyleSheet.create({
  radarSection: {
    marginTop: 24,
    alignItems: "center",
    justifyContent: "center",
  },
  radarContainer: {
    alignItems: "center",
    justifyContent: "center",
  },
});

type IconComponent = React.FC<{ x?: number; y?: number }>;

type RadarMetricPDF = {
  label: string;
  value: number;
  icon: IconComponent;
};

const getRiskValue = (risk: CustomRiskLevel): number => {
  switch (risk) {
    case CustomRiskLevel.LOW:
      return 33;
    case CustomRiskLevel.MEDIUM:
    case CustomRiskLevel.NORMAL:
      return 66;
    case CustomRiskLevel.HIGH:
      return 100;
    default:
      return 0;
  }
};

type RadarChartPDFProps = {
  folderRisks: CartographyRisks;
};

export const RadarChartPDF = ({ folderRisks }: RadarChartPDFProps) => {
  const { t } = useTranslation();

  const { activity, location, client, mission } = folderRisks;

  const data: RadarMetricPDF[] = [
    {
      label: t("folder.cartographie.radar.location"),
      icon: PdfLocationIcon,
      value: getRiskValue(location),
    },
    {
      label: t("folder.cartographie.radar.client"),
      icon: PdfClientIcon,
      value: getRiskValue(client),
    },
    {
      label: t("folder.cartographie.radar.mission"),
      icon: PdfMissionIcon,
      value: getRiskValue(mission),
    },
    {
      label: t("folder.cartographie.radar.activity"),
      icon: PdfActivityIcon,
      value: getRiskValue(activity),
    },
  ];

  const size = 200;
  const padding = 40;
  const radius = size / 2 - padding;
  const center = size / 2;
  const angleSlice = (2 * Math.PI) / data.length;

  const getPoint = (value: number, index: number, scale = 1) => {
    const angle = index * angleSlice - Math.PI / 2;
    const r = (value / 100) * radius * scale;
    const x = center + r * Math.cos(angle);
    const y = center + r * Math.sin(angle);
    return { x, y };
  };

  const polygonPoints = data
    .map((d, i) => {
      const point = getPoint(d.value, i);
      return `${point.x},${point.y}`;
    })
    .join(" ");

  const getDotColor = (value: number): string => {
    if (value === 0) return "#9FA6BC";
    if (value === 33) return "#00D6A8";
    if (value === 66) return "#F3AC60";
    return "#FE6447";
  };

  const iconOffset = 100;
  const iconPositions = data.map((_, i) => getPoint(iconOffset, i));

  const iconTransforms = [
    { dx: -10, dy: -30, iconT: { x: 0, y: 0 } },
    { dx: 0, dy: 0, iconT: { x: 10, y: -10 } },
    { dx: 0, dy: 0, iconT: { x: -8, y: 5 } },
    { dx: 0, dy: 0, iconT: { x: -25, y: -10 } },
  ];

  const labelTransforms = [
    { dx: -5, dy: 0, textAnchor: "middle" as const, textX: 10, textY: 25 },
    { dx: 5, dy: 5, textAnchor: "start" as const, textX: 9, textY: 15 },
    { dx: 0, dy: 0, textAnchor: "middle" as const, textX: 0, textY: 30 },
    { dx: 0, dy: 0, textAnchor: "end" as const, textX: -5, textY: 15 },
  ];

  return (
    <View style={styles.radarContainer}>
      <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Web lines */}
        {[0.33, 0.66, 1].map((scale, idx) => {
          const points = data
            .map((_, i) => {
              const point = getPoint(100, i, scale);
              return `${point.x},${point.y}`;
            })
            .join(" ");
          return <Polygon key={idx} points={points} fill="none" stroke="#B3B9CC" strokeWidth="1" />;
        })}

        {/* Central axes */}
        <Path
          d={`M ${center} ${center - radius} L ${center} ${center + radius}`}
          stroke="#B3B9CC"
          strokeWidth="1"
        />
        <Path
          d={`M ${center - radius} ${center} L ${center + radius} ${center}`}
          stroke="#B3B9CC"
          strokeWidth="1"
        />

        {/* Radar shape */}
        <Polygon points={polygonPoints} fill="#00A8E4" fillOpacity={0.5} />

        {/* Dots */}
        {data.map((d, i) => {
          const point = getPoint(d.value, i);
          const color = getDotColor(d.value);
          return <Circle key={i} cx={point.x} cy={point.y} r={6} fill={color} />;
        })}

        {/* Icons and labels */}
        {iconPositions.map((pos, i) => {
          const iconTransform = iconTransforms[i];
          const labelTransform = labelTransforms[i];
          const Icon = data[i].icon;

          return (
            <G
              key={i}
              transform={`translate(${pos.x + iconTransform.dx}, ${pos.y + iconTransform.dy})`}
            >
              <G transform={`translate(${iconTransform.iconT.x}, ${iconTransform.iconT.y})`}>
                <Icon x={0} y={0} />
              </G>
              <Text
                x={labelTransform.textX}
                y={labelTransform.textY}
                fill="#0A1A53"
                textAnchor={labelTransform.textAnchor}
                style={{ fontSize: 9 }}
              >
                {data[i].label}
              </Text>
            </G>
          );
        })}
      </Svg>
    </View>
  );
};
